import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Group, Layout, Page, Button, Grid } from "@/comn/components";
import {
    useFetch,
    useForm,
    useModal,
    useToast,
    useResource,
    usePage,
    TFormSchema,
    useAuth,
    useGrid,
} from "@/comn/hooks";
import { OSCI0103018Q } from "@/tz/go/egaz/osci/ptl/usr/orgusr/OSCI0103018Q";
import { APIS, URLS, SF_ORG_USR_INFO, SG_USR_AUTH_LIST } from "./services/OsciOrgUsrAprvService";
import { ptlUtils } from "../../comn";

/*
 * registration for Organization User Approve
 * !== 기관 사용자 신청 승인/반려 ==!
 */
export const OSCI0103022S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0002"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { usrIdntfId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    const auth = useAuth();
    const [ogaRppnYn, setOgaRppnYn] = useState<"Y" | "N">("N");
    const [pTin, setPTin] = useState();
    // 신청 처리상태 : U=미처리, R=처리완료(승인 또는 거절) - 매퍼 getOrgUsrAplyInfo 의 STATUS
    const [aplyStatus, setAplyStatus] = useState<string>("");

    useResource({
        defaultSchema: [{ area: "orgCd" }, { area: "cntyCd" }, { area: "orgDeptCd" }, { area: "cstmCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization Information Form !== 기관 정보 form ==!
        orgUsrInfo: useForm({
            defaultSchema: SF_ORG_USR_INFO,
            defaultValues: {},
        }),
    };

    const grid = {
        ogaUsrAuthList: useGrid({ defaultSchema: SG_USR_AUTH_LIST }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Check Organization User Information !== 기관 사용자 정보 조회 ==!
        getOrgUsrAprvInfo: useFetch({
            api: (data) => APIS.getOrgUsrAprvInfo(usrIdntfId),
            enabled: !!usrIdntfId,
            onSuccess: (data) => {
                const orgUsrInfo = data.orgUsrInfo.content;
                form.orgUsrInfo.setValues(orgUsrInfo);
                setPTin(orgUsrInfo.psnTin);
                setAplyStatus(orgUsrInfo.status);

                // Organization User Auth list !== 기관 사용자 권한 목록 ==!
                const ogaUsrAuthList = orgUsrInfo.ogaUsrAuthList;
                if (ogaUsrAuthList != null) grid.ogaUsrAuthList.setData(comnUtils.getGridData(ogaUsrAuthList));
            },
            onError: (error) => {},
            showToast: true,
        }),

        executeUsrAprv: useFetch({
            api: (data) => APIS.executeUsrAprv({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00075", // Successfully approved., !== 정상적으로 승인되었습니다. ==!
                    onConfirm: () => {
                        navigate(URLS.osci0103021q);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.orgUsrInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: "msg.osc.00015",
                });
            },
        }),

        executeUsrRtrn: useFetch({
            api: (data) => APIS.executeUsrRtrn(data),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        navigate(URLS.osci0103021q);
                    },
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    form.orgUsrInfo.setErrors(error.response.data.errors);
                }
                modal.openModal({
                    content: "msg.osc.00015",
                });
            },
        }),

        saveOgaUsrAuthList: useFetch({
            api: (data) =>
                APIS.saveOgaUsrAuthList({
                    tin: pTin,
                    usrAuthList: grid.ogaUsrAuthList.getData({ excludes: ["origin"] }),
                }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006",
                });
                fetch.getOrgUsrAprvInfo.fetch(usrIdntfId);
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    form.orgUsrInfo.setErrors(error.response.data.errors);
                }
                modal.openModal({
                    content: "msg.osc.00015",
                });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click List Button !== 목록 버튼 클릭 ==!
        onListBtnClick: () => {
            navigate(`${URLS.osci0103021q}`);
        },

        // Execute Organization User Approval !== 기관 사용자 직원 승인 ==!
        executeUsrAprv: () => {
            if (!comnUtils.isEmpty(form.orgUsrInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.orgUsrInfo.handleSubmit(
                () => {
                    const data = form.orgUsrInfo.getValues();
                    data.aprvYn = "Y";

                    const saveUsrAuthData = grid.ogaUsrAuthList.getData({ excludes: ["origin"] });
                    data.ogaUsrAuthList = saveUsrAuthData;

                    modal.openModal({
                        content: "msg.osc.00067", // Confirm approve? !== 승인하시겠습니까? ==!
                        onConfirm: () => {
                            fetch.executeUsrAprv.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },

        // Execute Company Employee Return !== 업체 직원 반려 ==!
        executeEmpRtrn: () => {
            if (!comnUtils.isEmpty(form.orgUsrInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.orgUsrInfo.handleSubmit(
                () => {
                    const formData = form.orgUsrInfo.getValues();
                    formData.aprvYn = "N";
                    formData.aprvOrgYn = aprvOrgYn;

                    const saveUsrAuthData = grid.ogaUsrAuthList.getData({ excludes: ["origin"] });
                    formData.ogaUsrAuthList = saveUsrAuthData;

                    if (aprvOrgYn == "Y") {
                        const handlePopup = (data: any) => {
                            formData.rtrnRsn = data;

                            modal.closeModal();

                            fetch.executeUsrRtrn.fetch(formData);
                        };

                        modal.openModal({
                            content: <ModalRtrnRsn handlePopup={handlePopup} />,
                            size: "sm",
                            draggable: true,
                        });
                    } else {
                        formData.rtrnRsn = "Reject";
                        modal.openModal({
                            content: "msg.osc.00066", // Confirm return? !== 반려하시겠습니까? ==!
                            onConfirm: () => {
                                fetch.executeUsrRtrn.fetch(formData);
                            },
                        });
                    }
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },

        // Save Company Employee Auth !== 업체 직원 권한 저장 ==!
        saveOgaUsrAuthList: () => {
            const list = grid.ogaUsrAuthList.getData({ excludes: ["origin"] });
            if (list.length === 0) {
                modal.openModal({ content: "msg.osc.00218" }); // There is no data to be stored and deleted. !== 저장 및 삭제 대상 데이터가 없습니다. ==!
                return;
            }

            modal.openModal({
                content: "msg.osc.00064", // Confirm update? !== 수정하시겠습니까? ==!
                onConfirm: () => {
                    fetch.saveOgaUsrAuthList.fetch();
                },
            });
        },

        onBtnGrdAddRowClick: () => {
            const data = grid.ogaUsrAuthList.getData({ excludes: ["origin"] }); // INSERT, DELETE

            if (!comnUtils.isEmpty(data)) {
                modal.openModal({ content: "msg.osc.00219" }); // Data has been changed. Please save it and proceed. !== 데이터가 변경되었습니다. 저장 후 진행해 주세요. ==!
                return;
            }

            const modalCallback = (list: any) => {
                const authList = grid.ogaUsrAuthList.getData();
                list.forEach(
                    (item: any) =>
                        authList.findIndex((subItem: any) => item.authId === subItem.authId) === -1 &&
                        grid.ogaUsrAuthList.addRow(item),
                );
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "sm",
                content: (
                    <OSCI0103018Q
                        usrIdntfId={auth.get("usrIdntfId")}
                        ogaUsrAuthList={grid.ogaUsrAuthList.getData()}
                        callback={modalCallback}
                    />
                ),
            });
        },

        onBtnGrdDelRowClick: () => {
            if (comnUtils.isEmpty(grid.ogaUsrAuthList.getChecked())) {
                modal.openModal({ content: "msg.osc.00084" }); // No data has been selected.. !== 선택된 데이터가 없습니다. ==!
                return;
            }

            // 대표자 신청승인 상세(0103020S)와 같게 삭제 전 확인을 받는다. (결함관리대장 364번)
            modal.openModal({
                content: "msg.osc.00062", // Do you want to remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => {
                    grid.ogaUsrAuthList.deleteRow("checkbox");
                },
            });
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getOrgUsrAprvInfo.fetch();
        form.orgUsrInfo.setEditable(false);
    }, []);

    useEffect(() => {
        if (ptlUtils.hasAuth("auth_osci0003", auth)) {
            setOgaRppnYn("Y");
        } else if (ptlUtils.hasAuth("auth_osci0002", auth)) {
            setOgaRppnYn("Y");
        }
        // if (!ptlUtils.hasAuth("auth_osci0003", auth)) {
        //     const raw = auth.get("ogaRppnYn") ?? auth.get("orgRppnYn");
        //     setOgaRppnYn(
        //         String(raw ?? "N")
        //             .trim()
        //             .toUpperCase() === "Y"
        //             ? "Y"
        //             : "N",
        //     );
        // } else {
        //     setOgaRppnYn("Y");
        // }
    }, [auth]);

    const aprvOrgYn = form.orgUsrInfo.watch("aprvOrgYn");

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.psnTin} />
                            <Group.Control {...form.orgUsrInfo.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.orgCd} />
                            <Group.Control {...form.orgUsrInfo.schema.deptCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.orgRppnYn} />
                            <Group.Control {...form.orgUsrInfo.schema.frgnrYn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.aplcCntyCd} />
                            <Group.Control {...form.orgUsrInfo.schema.usrMoblNo} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.usrEmlAddr} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.rsdtAddr} />
                        </Group.Row>
                        {/* Customs Office Code 항목 제외
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.cstmOfceCd} />
                        </Group.Row>
                        */}
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Title title={"T_OGA_USR_AUTH_LIST"} />
                    <Group.Section gap={2}>
                        {/* 이미 승인/거절된 건은 권한을 변경할 수 없도록 함 (aplyStatus U=미처리) */}
                        <Layout.Right hidden={aplyStatus !== "U"}>
                            <Button role={"gridAdd"} onClick={handler.onBtnGrdAddRowClick} />
                            <Button role={"gridDelete"} onClick={handler.onBtnGrdDelRowClick} />
                        </Layout.Right>

                        <Grid {...grid.ogaUsrAuthList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                </Layout.Left>

                {/* 이미 승인/거절된 건은 조회만 가능하도록 처리 버튼을 숨김 (aplyStatus U=미처리) */}
                <Layout.Right hidden={ogaRppnYn !== "Y" || aplyStatus !== "U"}>
                    <Layout.Button
                        role={"save"}
                        onClick={() => {
                            handler.saveOgaUsrAuthList();
                        }}
                    >
                        {t("B_SAVE")}
                    </Layout.Button>
                    <Layout.Button
                        role={"apply"}
                        onClick={() => {
                            handler.executeUsrAprv();
                        }}
                    >
                        {t("B_APRV")}
                    </Layout.Button>
                    <Layout.Button
                        color="error"
                        onClick={() => {
                            handler.executeEmpRtrn();
                        }}
                    >
                        {aprvOrgYn === "Y" ? t("B_RTRN") : t("B_RJCT")}
                    </Layout.Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};

/*
 * !== 반려사유 Modal ==!
 */
export const ModalRtrnRsn = (props: any) => {
    useResource({
        defaultSchema: [{ area: "bsopClsfCd" }, { area: "comnCd", comnCd: "OSC0088" }],
    });

    const { data, handlePopup } = props;

    // 현재 화면설계 참고 -> 항목과 검색어 입력폼 명은 개발에 맞게 변경 해주세요(현재는 라벨이없어서 임시로 넣어놓았습니다)
    const SG_FORM: TFormSchema = {
        id: "form",
        schema: {
            rtrnRsn: { type: "textarea", label: "L_RTRN_RSN", required: true, controlSize: 10, maxLength: 2000 },
        },
    };

    const form = useForm({
        defaultSchema: SG_FORM,
        defaultValues: { ...data },
    });

    const handler = {
        submitBtnClick: () => {
            handlePopup(form.getValue("rtrnRsn"));
        },
    };

    return (
        <>
            <Page>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.schema.rtrnRsn} />
                            </Group.Row>
                        </Group.Section>
                        <Group.Section>
                            <Layout>
                                <Layout.Right>
                                    <Button
                                        role={"submit"}
                                        onClick={() => {
                                            handler.submitBtnClick();
                                        }}
                                    />
                                </Layout.Right>
                            </Layout>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </Page>
        </>
    );
};
